import { useState, FormEvent, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Settings, Database, Trash2 } from 'lucide-react'
import Toast from '@/components/Toast'

interface EditProps {
  account: {
    id: number
    name: string
    processing_label: string | null
    gmail_session_id: number
    storage_window_days: number
    purge_emails: boolean
    purge_processing_logs: boolean
    purge_unblocked_senders: boolean
    purge_blocked_senders: boolean
    purge_solid_queue: boolean
    purge_email_fetch_logs: boolean
  }
  errors?: string[]
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
  flash?: {
    notice?: string
    alert?: string
  }
}

export default function Edit({ account, errors, currentUser, gmailConnection, flash }: EditProps) {
  const [processingLabel, setProcessingLabel] = useState(account.processing_label || '')
  const [gmailSessionId, setGmailSessionId] = useState((account.gmail_session_id || 0).toString())
  const [storageWindowDays, setStorageWindowDays] = useState(account.storage_window_days.toString())

  // Data retention settings
  const [purgeEmails, setPurgeEmails] = useState(account.purge_emails)
  const [purgeProcessingLogs, setPurgeProcessingLogs] = useState(account.purge_processing_logs)
  const [purgeUnblockedSenders, setPurgeUnblockedSenders] = useState(account.purge_unblocked_senders)
  const [purgeBlockedSenders, setPurgeBlockedSenders] = useState(account.purge_blocked_senders)
  const [purgeSolidQueue, setPurgeSolidQueue] = useState(account.purge_solid_queue)
  const [purgeEmailFetchLogs, setPurgeEmailFetchLogs] = useState(account.purge_email_fetch_logs)

  // Manual purge settings
  const [manualPurgeDays, setManualPurgeDays] = useState('30')
  const [manualPurgeEmails, setManualPurgeEmails] = useState(true)
  const [manualPurgeProcessingLogs, setManualPurgeProcessingLogs] = useState(true)
  const [manualPurgeUnblockedSenders, setManualPurgeUnblockedSenders] = useState(true)
  const [manualPurgeBlockedSenders, setManualPurgeBlockedSenders] = useState(false)
  const [manualPurgeSolidQueue, setManualPurgeSolidQueue] = useState(true)
  const [manualPurgeEmailFetchLogs, setManualPurgeEmailFetchLogs] = useState(true)

  const [isPurging, setIsPurging] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    if (flash?.notice) {
      setToastMessage(flash.notice)
      setToastType('success')
      setShowToast(true)
    } else if (flash?.alert) {
      setToastMessage(flash.alert)
      setToastType('error')
      setShowToast(true)
    }
  }, [flash])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.put(`/accounts/${account.id}/settings`, {
      account: {
        processing_label: processingLabel.trim() || null,
        gmail_session_id: parseInt(gmailSessionId, 10) || 0,
        storage_window_days: parseInt(storageWindowDays, 10) || 7,
        purge_emails: purgeEmails,
        purge_processing_logs: purgeProcessingLogs,
        purge_unblocked_senders: purgeUnblockedSenders,
        purge_blocked_senders: purgeBlockedSenders,
        purge_solid_queue: purgeSolidQueue,
        purge_email_fetch_logs: purgeEmailFetchLogs
      }
    })
  }

  const handleManualPurge = () => {
    if (!confirm('Are you sure you want to purge data? This action is PERMANENT and cannot be undone. Your emails will remain intact in Gmail - this only deletes your data from FilterHawk.')) {
      return
    }

    setIsPurging(true)
    router.post(`/accounts/${account.id}/settings/purge`, {
      days: parseInt(manualPurgeDays, 10) || 0,
      purge_emails: manualPurgeEmails,
      purge_processing_logs: manualPurgeProcessingLogs,
      purge_unblocked_senders: manualPurgeUnblockedSenders,
      purge_blocked_senders: manualPurgeBlockedSenders,
      purge_solid_queue: manualPurgeSolidQueue,
      purge_email_fetch_logs: manualPurgeEmailFetchLogs
    }, {
      preserveScroll: true,
      onFinish: () => setIsPurging(false)
    })
  }

  return (
    <>
      <Head title="Account Settings" />
      <DashboardLayout currentUser={currentUser} gmailConnection={gmailConnection} account={account}>
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
            Account Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your account configuration
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Settings className="w-5 h-5" />
              Processing Label
            </CardTitle>
            <CardDescription>
              Configure a Gmail label to be applied to emails while they are being processed.
              This label will be automatically applied when processing starts and removed when processing completes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors && errors.length > 0 && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
                  <div className="text-sm text-red-800 dark:text-red-200">
                    <ul className="list-disc list-inside space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="processing_label">
                  Processing Label Name
                </Label>
                <Input
                  id="processing_label"
                  type="text"
                  value={processingLabel}
                  onChange={(e) => setProcessingLabel(e.target.value)}
                  placeholder="e.g., FilterHawk/Processing"
                  className="max-w-md"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Leave blank to disable processing labels. If the label doesn't exist in your Gmail account, it will be created automatically.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="gmail_session_id">
                  Gmail Session ID
                </Label>
                <Input
                  id="gmail_session_id"
                  type="number"
                  min="0"
                  value={gmailSessionId}
                  onChange={(e) => setGmailSessionId(e.target.value)}
                  placeholder="0"
                  className="max-w-md"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  If you use multiple Gmail accounts, enter the session ID (0, 1, 2, etc.) to ensure Gmail links open in the correct account. Default is 0.
                </p>
              </div>

              <div className="flex gap-3">
                <Button type="submit">
                  Save Settings
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => router.visit('/')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Database className="w-5 h-5" />
              Data Retention Settings
            </CardTitle>
            <CardDescription>
              Configure how long data is kept in FilterHawk and what gets purged automatically.
              Changes to these settings will take effect during the next nightly purge job.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="storage_window_days">
                  Storage Window (Days)
                </Label>
                <Input
                  id="storage_window_days"
                  type="number"
                  min="1"
                  max="365"
                  value={storageWindowDays}
                  onChange={(e) => setStorageWindowDays(e.target.value)}
                  className="max-w-md"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Number of days to keep data before automatic purging (1-365 days). Default is 7 days.
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select which data types to purge automatically:
                </p>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_emails"
                    checked={purgeEmails}
                    onCheckedChange={(checked) => setPurgeEmails(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_emails" className="cursor-pointer">
                      Emails
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge email records older than the storage window. Associated processing logs will also be purged.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_processing_logs"
                    checked={purgeProcessingLogs}
                    onCheckedChange={(checked) => setPurgeProcessingLogs(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_processing_logs" className="cursor-pointer">
                      Processing Logs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge processing log records older than the storage window.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_unblocked_senders"
                    checked={purgeUnblockedSenders}
                    onCheckedChange={(checked) => setPurgeUnblockedSenders(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_unblocked_senders" className="cursor-pointer">
                      Unblocked Senders
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge unblocked sender records not seen within the storage window.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_blocked_senders"
                    checked={purgeBlockedSenders}
                    onCheckedChange={(checked) => setPurgeBlockedSenders(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_blocked_senders" className="cursor-pointer">
                      Blocked Senders
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge blocked sender records not seen within the storage window. Leave unchecked to maintain your blocked list.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_solid_queue"
                    checked={purgeSolidQueue}
                    onCheckedChange={(checked) => setPurgeSolidQueue(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_solid_queue" className="cursor-pointer">
                      Background Jobs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge completed background job records older than the storage window.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="purge_email_fetch_logs"
                    checked={purgeEmailFetchLogs}
                    onCheckedChange={(checked) => setPurgeEmailFetchLogs(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="purge_email_fetch_logs" className="cursor-pointer">
                      Email Fetch Logs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge email fetch log records older than the storage window. These logs track all emails fetched from Gmail.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit">
                  Save Retention Settings
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
              <Trash2 className="w-5 h-5" />
              Manual Data Purge
            </CardTitle>
            <CardDescription>
              Immediately purge data older than a specified number of days.
              Set to 0 to purge all selected data from your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4">
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Warning:</strong> This action is permanent and cannot be undone.
                  Your emails will remain intact in Gmail - this only deletes your data from FilterHawk.
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="manual_purge_days">
                  Purge data older than (days)
                </Label>
                <Input
                  id="manual_purge_days"
                  type="number"
                  min="0"
                  value={manualPurgeDays}
                  onChange={(e) => setManualPurgeDays(e.target.value)}
                  className="max-w-md"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enter 0 to purge all selected data from your account.
                </p>
              </div>

              <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select which data types to purge:
                </p>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_emails"
                    checked={manualPurgeEmails}
                    onCheckedChange={(checked) => setManualPurgeEmails(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_emails" className="cursor-pointer">
                      Emails
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge email records. Associated processing logs will also be purged.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_processing_logs"
                    checked={manualPurgeProcessingLogs}
                    onCheckedChange={(checked) => setManualPurgeProcessingLogs(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_processing_logs" className="cursor-pointer">
                      Processing Logs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge processing log records.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_unblocked_senders"
                    checked={manualPurgeUnblockedSenders}
                    onCheckedChange={(checked) => setManualPurgeUnblockedSenders(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_unblocked_senders" className="cursor-pointer">
                      Unblocked Senders
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge unblocked sender records.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_blocked_senders"
                    checked={manualPurgeBlockedSenders}
                    onCheckedChange={(checked) => setManualPurgeBlockedSenders(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_blocked_senders" className="cursor-pointer">
                      Blocked Senders
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge blocked sender records.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_solid_queue"
                    checked={manualPurgeSolidQueue}
                    onCheckedChange={(checked) => setManualPurgeSolidQueue(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_solid_queue" className="cursor-pointer">
                      Background Jobs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge completed background job records.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="manual_purge_email_fetch_logs"
                    checked={manualPurgeEmailFetchLogs}
                    onCheckedChange={(checked) => setManualPurgeEmailFetchLogs(checked as boolean)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="manual_purge_email_fetch_logs" className="cursor-pointer">
                      Email Fetch Logs
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Purge email fetch log records.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleManualPurge}
                  disabled={isPurging}
                >
                  {isPurging ? 'Purging...' : 'Purge Data Now'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    </>
  )
}
